/**
 * DataGo — visibility driven events.
 *
 * Covers only what the Web Pixels API cannot report on its own:
 *
 *   [data-dg-item] inside [data-dg-list]  -> view_item_list, batched per list
 *   [data-dg-block]                       -> view_block  / click_block
 *   [data-dg-promotion]                   -> view_promotion / select_promotion
 *
 * Every list is reported from here, catalog and search included. Shopify's
 * `collection_viewed` / `search_submitted` are not used for this: they hand the
 * pixel a reduced product with no tags, option values or compare-at price, and
 * `collection_viewed` has been observed returning zero variants for a populated
 * collection. A card without a `[data-dg-list]` ancestor is still ignored rather
 * than guessed at.
 *
 * `data-dg-block` and `data-dg-promotion` hold a JSON object that is passed
 * straight through, so a section decides its own parameters without this file
 * needing to know about it.
 */
(function () {
  // GCC found ~26% duplicate select_promotion hits (vs 0.6% on RU) — the
  // most likely cause is this script's click listener getting attached more
  // than once (a theme re-render, an app block reload, etc. re-executing
  // this IIFE without a full page navigation). Guard against that.
  if (window.__dataGoViewObserverInitialized) return;
  window.__dataGoViewObserverInitialized = true;

  const ITEM_SELECTOR = '[data-dg-item]';
  const LIST_SELECTOR = '[data-dg-list]';
  const BLOCK_SELECTOR = '[data-dg-block]';
  const PROMOTION_SELECTOR = '[data-dg-promotion]';
  const TRACKED_SELECTOR = `${ITEM_SELECTOR}, ${BLOCK_SELECTOR}, ${PROMOTION_SELECTOR}`;

  // Swiper duplicates slides for looping. A cloned banner carries the same
  // promotion_id as the original, so counting clones inflates impressions and
  // shifts every creative_slot.
  const CLONE_SELECTOR = '.swiper-slide-duplicate';

  const VISIBLE_RATIO = 0.5;
  const FLUSH_DELAY = 300;

  /** @type {Map<Element, {config: object, items: object[]}>} */
  const pending = new Map();
  const registered = new WeakSet();

  let observer = null;
  let flushTimer = null;

  /**
   * @param {string|undefined} value
   * @returns {object}
   */
  const parseConfig = (value) => {
    if (!value) return {};

    try {
      return JSON.parse(value);
    } catch (error) {
      console.error('[DataGo] malformed tracking attribute:', value);
      return {};
    }
  };

  /**
   * Position of a card among the cards of its list.
   *
   * @param {Element} card
   * @param {Element} container
   * @returns {number|null}
   */
  const indexOf = (card, container) => {
    const position = Array.from(container.querySelectorAll(ITEM_SELECTOR)).indexOf(card);

    return position === -1 ? null : position + 1;
  };

  const scheduleFlush = () => {
    if (flushTimer) return;
    flushTimer = setTimeout(flush, FLUSH_DELAY);
  };

  /**
   * Emits one `view_item_list` per list that collected cards since the last flush.
   *
   * @returns {Promise<void>}
   */
  async function flush() {
    flushTimer = null;
    if (!window.DataGo) return;

    const batches = Array.from(pending.values());
    pending.clear();

    for (const batch of batches) {
      if (!batch.items.length) continue;

      // Anything the list declares beyond its identity — section_code, section_id,
      // section_name, search_term — travels with the event parameters.
      const { item_list_id = null, item_list_name = null, ...params } = batch.config;

      await window.DataGo.push(
        'view_item_list',
        { action: 'view_item_list', ...params },
        { item_list_id, item_list_name, items: batch.items }
      );
    }
  }

  /**
   * @param {Element} card
   */
  const collectItem = (card) => {
    const container = card.closest(LIST_SELECTOR);
    if (!container) return;

    const item = parseConfig(card.dataset.dgItem);
    if (!item.item_id) return;

    const config = parseConfig(container.dataset.dgList);
    const batch = pending.get(container) || { config, items: [] };

    batch.items.push({
      ...item,
      index: indexOf(card, container),
      item_list_id: config.item_list_id ?? null,
      item_list_name: config.item_list_name ?? null,
    });

    pending.set(container, batch);
    scheduleFlush();
  };

  /**
   * @param {Element} element
   * @param {string} event - `view_block` or `click_block`.
   */
  const sendBlock = (element, event) => {
    if (!window.DataGo) return;

    window.DataGo.push(event, parseConfig(element.dataset.dgBlock));
  };

  /**
   * @param {Element} element
   * @param {string} event - `view_promotion` or `select_promotion`.
   */
  const sendPromotion = (element, event) => {
    if (!window.DataGo) return;

    const promotion = parseConfig(element.dataset.dgPromotion);

    // A block cannot know its own position, so fall back to DOM order.
    const slot =
      promotion.creative_slot ??
      Array.from(document.querySelectorAll(PROMOTION_SELECTOR))
        .filter((candidate) => !candidate.closest(CLONE_SELECTOR))
        .indexOf(element) + 1;

    // The same banner can appear in more than one slide, so a named promotion is
    // reported once. An unnamed one falls back to once per element.
    if (event === 'view_promotion' && promotion.promotion_id) {
      if (!window.DataGo.viewOnce(`view_promotion:${promotion.promotion_id}`)) return;
    }

    window.DataGo.push(
      event,
      { banner_department: promotion.banner_department ?? null },
      {
        promotion_id: promotion.promotion_id ?? null,
        promotion_name: promotion.promotion_name ?? null,
        creative_name: promotion.creative_name ?? null,
        creative_slot: slot,
        items: [],
      }
    );
  };

  /**
   * @param {IntersectionObserverEntry[]} entries
   */
  const onIntersect = (entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;

      const element = entry.target;
      observer.unobserve(element);

      if (element.hasAttribute('data-dg-item')) collectItem(element);
      if (element.hasAttribute('data-dg-block')) sendBlock(element, 'view_block');
      if (element.hasAttribute('data-dg-promotion')) sendPromotion(element, 'view_promotion');
    }
  };

  /**
   * A card only counts when it sits in an annotated list, so cards on the catalog
   * and search pages are never observed at all.
   *
   * @param {Element} element
   * @returns {boolean}
   */
  const isTracked = (element) => {
    if (element.closest(CLONE_SELECTOR)) return false;
    if (!element.hasAttribute('data-dg-item')) return true;

    return Boolean(element.closest(LIST_SELECTOR));
  };

  /**
   * @param {Element} element
   */
  const observeElement = (element) => {
    if (registered.has(element) || !isTracked(element)) return;

    registered.add(element);
    observer.observe(element);
  };

  /**
   * @param {ParentNode} root
   */
  const observeWithin = (root) => {
    if (!root.querySelectorAll) return;

    for (const element of root.querySelectorAll(TRACKED_SELECTOR)) observeElement(element);
  };

  /**
   * @param {Event} event
   */
  const onClick = (event) => {
    if (!(event.target instanceof Element)) return;
    // A click on a cloned slide is the same banner — report it, but never twice.
    if (event.target.closest(CLONE_SELECTOR)) return;

    const promotion = event.target.closest(PROMOTION_SELECTOR);
    if (promotion) sendPromotion(promotion, 'select_promotion');

    const block = event.target.closest(BLOCK_SELECTOR);
    if (block) sendBlock(block, 'click_block');
  };

  const init = () => {
    observer = new IntersectionObserver(onIntersect, { threshold: VISIBLE_RATIO });
    observeWithin(document);

    // Shelves load their products after the page does, and grids are re-rendered
    // on filtering, sorting and pagination.
    const mutations = new MutationObserver((records) => {
      for (const record of records) {
        for (const node of record.addedNodes) {
          if (node.nodeType !== 1) continue;

          if (node.matches(TRACKED_SELECTOR)) observeElement(node);
          observeWithin(node);
        }
      }
    });
    mutations.observe(document.body, { childList: true, subtree: true });

    document.addEventListener('click', onClick);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
